import type { OrderState } from "@store/slices/LEGACY__orderSlice";
import type { Currency } from "@store/types/Currency.types";
import type { ServerData, ServerDataFetchStatus } from "@store/types/ServerData.types";

import { type Address } from "@jti/fe-common";
import type {
  CartCoupon,
  IAvailablePaymentMethod,
  ICartItemErrorType,
  OrderPrices,
  Product as CartItem,
} from "@jti/magento";
import type { MagentoPrice } from "@jti/magento-types";

export type CartCountry = {
  id: string;
  twoLettersAbbr: string;
  threeLettersAbbr: string;
  fullNameLocale: string;
  fullNameEnglish: string;
};

export type CartStoreConfig = {
  store_code: string;
  store_group_code: string;
  free_shipping_threshold?: number | null;
  is_order_limit_enabled: boolean;
  is_age_verification_enabled: boolean;
  order_limit_value: number;
  base_currency_code: string;
  is_free_gifts_enabled: boolean;
  gift_basket_quantity_limitation: string;
  postcode_is_required: boolean;
  postcode_max_length: number;
  postcode_min_length: number;
  postcode_validate_regex: string;
  postcode_validation_enabled: boolean;
  allow_empty_firstname: boolean;
  cartons_limit_value: number;
};

export type FreeGiftError = {
  errorCode: ICartItemErrorType;
  parentId?: string;
  parentSku?: string;
};

export type DiscontinuedProductError = {
  sku: string;
  name: string;
};

export type AvailableFreeGiftProduct = {
  id: number;
  sku: string;
  name: string;
};

export type AvailableFreeGift = {
  sales_rule_id: number;
  allowed_gift_products: AvailableFreeGiftProduct[];
  gift_qty_limit: number;
  is_removable: boolean;
};

export type CartSliceState = {
  id?: string;
  items: CartItem[];
  oldItems?: CartItem[];
  countries: CartCountry[];
  prices: ServerData<OrderPrices | null>;
  coupons: CartCoupon[];
  billingAddress?: Address;
  couponsPending: boolean;
  availablePaymentMethods: Pick<IAvailablePaymentMethod, "code" | "title">[];
  itemsPending: boolean;
  pendingItemsCartId: string | null;
  qtyPending: boolean;
  notLoggedIn: boolean;
  tradeInErrorMessage?: string;
  couponErrors?: string[] | [];
  freeGiftsErrors?: FreeGiftError[];
  discontinuedProductsErrors?: DiscontinuedProductError[];
  currency: Currency;
  fetchCurrencyStatus: ServerDataFetchStatus;
  promoCode: {
    isVisible: boolean;
    isLimitReached: boolean;
  };
  storeConfig: CartStoreConfig;
  fetchStoreConfigStatus: ServerDataFetchStatus;
  order: OrderState;
  isZeroValueOrder: boolean;
  isFailedPaymentFlow: boolean;
  isSubscription: boolean;
  isFreeTrial: boolean | undefined;
  minimumPayableAmount?: MagentoPrice & { isReached?: boolean };
  isLoadingCartData: boolean;
  availableFreeGifts: AvailableFreeGift[];
};
